import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { VisualEmailEditor, type VisualEmailEditorHandle } from '../components/VisualEmailEditor';

interface Template {
  subject: string;
  html: string;
  text: string;
}

interface Variable {
  name: string;
  kind: 'value' | 'flag' | 'list';
  fields?: string[];
}

interface TemplateDetail {
  type: string;
  label: string;
  description: string;
  customized: boolean;
  updated_at: string | null;
  updated_by: string | null;
  template: Template;
  default: Template;
  variables: Variable[];
  sample: Record<string, unknown>;
}

interface Preview {
  rendered: Template;
  unknown_variables: string[];
}

type Tab = 'visual' | 'html' | 'text';
type Target = 'subject' | Tab;

const TABS: { id: Tab; label: string }[] = [
  { id: 'visual', label: 'Visual' },
  { id: 'html', label: 'HTML source' },
  { id: 'text', label: 'Plain text' },
];

const same = (a: Template, b: Template) => a.subject === b.subject && a.html === b.html && a.text === b.text;

export function EmailEdit() {
  const { type = '' } = useParams();
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [draft, setDraft] = useState<Template | null>(null);
  const [tab, setTab] = useState<Tab>('visual');
  // Bumped whenever the HTML changes outside the visual editor, so it rebuilds.
  const [visualKey, setVisualKey] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  // Where "insert variable" goes: the last field the cursor was in.
  const lastTarget = useRef<Target>('visual');
  const visual = useRef<VisualEmailEditorHandle>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const sourceRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api
      .get<TemplateDetail>(`/api/admin/emails/${type}`)
      .then((d) => {
        setDetail(d);
        setDraft(d.template);
        setVisualKey((k) => k + 1);
      })
      .catch((err) => setMessage({ text: err.message, error: true }));
  }, [type]);

  // Debounced server render, so the preview uses the email worker's exact Mustache rules.
  useEffect(() => {
    if (!draft) return;
    const id = setTimeout(() => {
      api
        .post<Preview>(`/api/admin/emails/${type}/preview`, draft)
        .then((p) => {
          setPreview(p);
          setPreviewError(null);
        })
        .catch((err) => setPreviewError(err.message));
    }, 400);
    return () => clearTimeout(id);
  }, [draft, type]);

  const dirty = Boolean(detail && draft && !same(detail.template, draft));

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (!detail || !draft) return <p>{message?.text ?? 'Loading…'}</p>;

  const set = (field: keyof Template, value: string) => setDraft((d) => (d ? { ...d, [field]: value } : d));

  const switchTab = (t: Tab) => {
    if (t === 'visual' && tab !== 'visual') setVisualKey((k) => k + 1);
    setTab(t);
    lastTarget.current = t;
  };

  const replaceDraft = (t: Template) => {
    setDraft(t);
    setVisualKey((k) => k + 1);
  };

  const insertIntoField = (el: HTMLInputElement | HTMLTextAreaElement | null, field: keyof Template, snippet: string) => {
    const value = draft[field];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    set(field, value.slice(0, start) + snippet + value.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  };

  const insert = (v: Variable) => {
    const target = lastTarget.current === 'subject' ? 'subject' : tab;
    const snippet =
      v.kind === 'list'
        ? `{{#${v.name}}}${(v.fields || []).map((f) => `{{${f}}}`).join(' ')}{{/${v.name}}}`
        : v.kind === 'flag'
          ? `{{#${v.name}}}{{/${v.name}}}`
          : `{{${v.name}}}`;
    if (target === 'subject') return insertIntoField(subjectRef.current, 'subject', snippet);
    if (target === 'visual') {
      if (!visual.current?.insertVariable(snippet, v.name)) {
        setMessage({ text: 'Click into a paragraph in the email first, then pick a variable.', error: true });
      }
      return;
    }
    insertIntoField(sourceRef.current, target, snippet);
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const saved = await api.put<{ updated_at: string; updated_by: string | null; template: Template }>(
        `/api/admin/emails/${type}`,
        draft
      );
      setDetail({ ...detail, customized: true, ...saved });
      setMessage({ text: 'Saved. New emails use this version within about a minute.', error: false });
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  // Sends the current draft (saved or not) with sample data to the signed-in admin.
  const sendTest = async () => {
    setSendingTest(true);
    setMessage(null);
    try {
      const d = await api.post<{ to: string }>(`/api/admin/emails/${type}/test`, draft);
      setMessage({ text: `Test sent to ${d.to}. It should arrive within a minute.`, error: false });
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Test send failed', error: true });
    } finally {
      setSendingTest(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this email to the original version? Your edits will be discarded.')) return;
    setBusy(true);
    setMessage(null);
    try {
      const d = await api.delete<{ template: Template }>(`/api/admin/emails/${type}`);
      setDetail({ ...detail, customized: false, updated_at: null, updated_by: null, template: d.template });
      replaceDraft(d.template);
      setMessage({ text: 'Reset to the original version.', error: false });
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Reset failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  // In the visual editor only plain values make sense inline; lists and flags are HTML-source edits.
  const insertable = tab === 'visual' ? detail.variables.filter((v) => v.kind === 'value') : detail.variables;

  return (
    <div>
      <Link to="/emails" style={{ fontSize: 13, color: '#6b7280' }}>
        ← Emails
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 0' }}>
        {detail.label}
      </h1>
      <p style={muted}>
        {detail.description}{' '}
        {detail.customized
          ? `Edited ${formatDate(detail.updated_at)}${detail.updated_by ? ` by ${detail.updated_by}` : ''}.`
          : 'Using the original version.'}
      </p>

      <label style={{ display: 'block', marginTop: 20, maxWidth: 720 }}>
        <div style={label}>Subject</div>
        <input
          ref={subjectRef}
          value={draft.subject}
          onChange={(e) => set('subject', e.target.value)}
          onFocus={() => (lastTarget.current = 'subject')}
          style={{ ...input, width: '100%' }}
        />
        {preview && (
          <div style={muted}>
            Sends as: <span style={{ color: '#374151' }}>{preview.rendered.subject}</span>
          </div>
        )}
      </label>

      <div style={{ display: 'flex', gap: 4, margin: '20px 0 8px' }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => switchTab(t.id)} style={tab === t.id ? tabActive : tabIdle}>
            {t.label}
          </button>
        ))}
      </div>

      {preview && preview.unknown_variables.length > 0 && (
        <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 8px' }}>
          Not available for this email (will be blank): {preview.unknown_variables.map((n) => `{{${n}}}`).join(', ')}
        </p>
      )}
      {previewError && <p style={{ fontSize: 12, color: '#b91c1c', margin: '0 0 8px' }}>{previewError}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 24, alignItems: 'start' }}>
        <div>
          {tab === 'visual' && (
            <>
              <div style={toolbar}>
                <ToolButton label="Bold" onClick={() => visual.current?.command('bold')}>
                  <strong>B</strong>
                </ToolButton>
                <ToolButton label="Italic" onClick={() => visual.current?.command('italic')}>
                  <em>I</em>
                </ToolButton>
                <ToolButton label="Add link" onClick={() => visual.current?.command('link')}>
                  Link
                </ToolButton>
                <ToolButton label="Remove link" onClick={() => visual.current?.command('unlink')}>
                  Unlink
                </ToolButton>
                <span style={{ ...muted, margin: '0 0 0 8px' }}>
                  Click any outlined text to edit it. Enter adds a line break.
                </span>
              </div>
              <VisualEmailEditor
                ref={visual}
                html={draft.html}
                sample={detail.sample}
                rebuildKey={visualKey}
                onChange={(html) => set('html', html)}
              />
              <p style={muted}>
                Item lists, prices and other parts that change per order show sample data and are edited in HTML
                source.
              </p>
            </>
          )}

          {tab !== 'visual' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
              <textarea
                key={tab}
                ref={sourceRef}
                value={draft[tab]}
                onChange={(e) => set(tab, e.target.value)}
                onFocus={() => (lastTarget.current = tab)}
                spellCheck={tab === 'text'}
                style={{ ...input, width: '100%', height: 560, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
              />
              {preview ? (
                tab === 'html' ? (
                  <iframe
                    title="Email preview"
                    // No scripts, no same-origin: the template is admin-authored HTML.
                    sandbox=""
                    srcDoc={preview.rendered.html}
                    style={{ width: '100%', height: 560, border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff' }}
                  />
                ) : (
                  <pre style={{ ...input, whiteSpace: 'pre-wrap', height: 560, overflow: 'auto', margin: 0, background: '#fff' }}>
                    {preview.rendered.text}
                  </pre>
                )
              ) : (
                <p style={muted}>Rendering…</p>
              )}
            </div>
          )}
          {tab === 'text' && (
            <p style={muted}>
              Shown by email apps that don't display HTML. If you change the wording in Visual, update it here too.
            </p>
          )}
        </div>

        <aside style={{ ...card, marginTop: 0, padding: 16 }}>
          <div style={label}>Insert a variable</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {insertable.map((v) => (
              <button
                key={v.name}
                type="button"
                style={chip}
                // Keep the editor's cursor where it is.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(v)}
                title={v.kind === 'list' ? `Repeats for each: ${(v.fields || []).join(', ')}` : undefined}
              >
                {v.kind === 'value' ? v.name : `${v.kind === 'list' ? 'each' : 'if'} ${v.name}`}
              </button>
            ))}
          </div>
          <p style={muted}>
            Variables fill in per email — for example <code>recipientName</code> becomes the customer's name.
            {tab !== 'visual' && (
              <>
                {' '}
                <code>{'{{#flag}}…{{/flag}}'}</code> shows text only when the flag is on;{' '}
                <code>{'{{^flag}}…{{/flag}}'}</code> when it's off. Lists repeat once per item.
              </>
            )}
          </p>
        </aside>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
        <button type="button" onClick={save} disabled={busy || !dirty} style={{ ...btnPrimary, opacity: busy || !dirty ? 0.5 : 1 }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => replaceDraft(detail.template)} disabled={busy || !dirty} style={btnSecondary}>
          Discard changes
        </button>
        <button
          type="button"
          onClick={sendTest}
          disabled={busy || sendingTest}
          title="Sends this version, with sample data, to your own admin email address"
          style={btnSecondary}
        >
          {sendingTest ? 'Sending…' : 'Send test to me'}
        </button>
        {detail.customized && (
          <button type="button" onClick={reset} disabled={busy} style={{ ...btnSecondary, color: '#b91c1c' }}>
            Reset to original
          </button>
        )}
        {message && <span style={{ fontSize: 13, color: message.error ? '#b91c1c' : '#15803d' }}>{message.text}</span>}
      </div>
    </div>
  );
}

function ToolButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // mousedown would steal focus (and the selection) from the editor iframe.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={toolBtn}
    >
      {children}
    </button>
  );
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 8,
  padding: 24,
  marginTop: 16,
};
const muted: React.CSSProperties = { fontSize: 12, color: '#6b7280', margin: '6px 0 0' };
const label: React.CSSProperties = { fontWeight: 500, fontSize: 14, marginBottom: 6 };
const input: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};
const toolbar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: 6,
  border: '1px solid #e5e5e5',
  borderBottom: 'none',
  borderRadius: '6px 6px 0 0',
  background: '#fff',
};
const toolBtn: React.CSSProperties = {
  minWidth: 32,
  height: 28,
  padding: '0 8px',
  border: '1px solid #e5e5e5',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
};
const chip: React.CSSProperties = {
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: 12,
  padding: '3px 8px',
  border: '1px solid #bfdbfe',
  borderRadius: 4,
  background: '#eff6ff',
  color: '#1e40af',
  cursor: 'pointer',
};
const tabIdle: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
};
const tabActive: React.CSSProperties = { ...tabIdle, background: '#1a1a1a', color: '#fff', borderColor: '#1a1a1a' };
const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSecondary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#fff',
  color: '#374151',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};
